import React, { memo } from 'react';
import { Flex } from 'antd-mobile';
import PropTypes from 'prop-types';
import { ImgWrap } from 'pubBiz';
import CornerMarker from 'pubBiz/CornerMarker';
import styles from './index.modules.less';
import { PriceText } from 'widget';
import {
  formatPrice,
  getPrecision
} from 'biz-micro-services-ui/project/middlePlatform/utils/formatMoney';
import Util from 'utils';

import grabImg from './asset/grab.png';

const defaultProps = {
  data: {},
  showSellPoint: false,
  sellPointList: [],
  productParams: {}
};

// sellPoint成员结构
const sellPointPropTypes = PropTypes.shape({
  sellPointColour: PropTypes.string, // 卖点颜色（不含#）
  sellPointName: PropTypes.string // 卖点文案
});

// data成员结构
const dataPropTypes = PropTypes.shape({
  id: PropTypes.string, // 商品ID
  itemId: PropTypes.string, // 商品ID（兼容字段）
  imgUrl: PropTypes.string, // 商品图片
  name: PropTypes.string, // 商品名称
  price: PropTypes.number, // 商品价格
  cornerMarkerRespDto: PropTypes.object, // 商品图片角标
  activityId: PropTypes.string, // 活动ID
  activityType: PropTypes.number, // 活动类型
  discountSalePrice: PropTypes.number, // 消费活动会员价
  savePrice: PropTypes.number, // 立省金额
  savePriceText: PropTypes.string, // 立省文案
  maxPrice: PropTypes.number, // 商品最高价格
  itemStyle: PropTypes.number, // 产品样式，0-默认，1-会员立省
  hasActivity: PropTypes.number, // 是否有活动，0-无，1-有
  type: PropTypes.number // 商品类型（6：多规格商品）
});

// props成员结构
const propTypes = {
  width: PropTypes.number.isRequired, // 商品展示宽度
  data: PropTypes.arrayOf(dataPropTypes),
  config: PropTypes.shape({
    reverse: PropTypes.bool, // 开启这个属性后，产品名称和卖点的顺序会互换
    radius: PropTypes.string // 圆角值
  }),
  showSellPoint: PropTypes.bool, // 是否显示卖点
  sellPointList: PropTypes.arrayOf(sellPointPropTypes),
  productParams: PropTypes.object, // 商品参数
  onPress: PropTypes.func, // 点击事件回调
  hasActivity: PropTypes.number // 是否有活动，0-无，1-有
};

const GoodItem = memo((props) => {
  const {
    width, // Number 宽度
    data, // Object 商品数据
    showSellPoint, // Boolean 是否显示卖点
    sellPointList, // Array 卖点
    config, // Object 配置
    productParams, // Object 商品参数
    onPress, // Function 点击事件
    hasActivity // Number 是否有活动，0-无，1-有
  } = props;

  const {
    imgUrl, // String 商品图片
    name, // String 商品名称
    cornerMarkerRespDto, // Object 商品图片角标
    price, // Number 商品价格
    discountSalePrice, // Number 消费活动会员价
    maxPrice, // Number 商品最高价格
    itemStyle, // Number 产品样式，0-默认，1-会员立省
    type, // Number 商品类型 type=6 多规格商品
    activityId = '',
    activityType = '',
    hasActivity: dataHasActivity
  } = data;
  const {
    reverse = false, // Boolean 开启这个属性后，产品名称和卖点的顺序会互换
    radius = 0 // String 圆角值
  } = config || {};
  // hasActivity：是否有活动，0-无，1-有；itemStyle：产品样式，0-默认，1-会员立省。
  // 只有活动楼层里的会员立省商品才切到新样式，其它商品继续保持老样式。
  const isConsumerActivity = (+hasActivity == 1 || +dataHasActivity == 1) && +itemStyle == 1;
  // 点击事件
  const handlePress = () => {
    const { id } = data;
    onPress && onPress();
    window.app.routerGoTo({
      pathname: `/shop/productDetail/${id}`,
      state: { ...productParams },
      search: Util.encodeQuery({
        activityId: activityId || '',
        activityType: activityType || ''
      })
    });
  };

  const renderSellPoint = () => {
    if (!showSellPoint) return null;

    return (
      <Flex direction="row" align="center" className={styles.sellPointContainer}>
        {(sellPointList || []).map((item, index) => {
          const { sellPointColour, sellPointName } = item;
          return (
            <Flex
              key={`sellPoint-${index}`}
              align="center"
              justify="center"
              className={styles.sellPointWrapper}
              style={{
                marginLeft: !index ? 0 : '0.08rem',
                border: `1px solid ${sellPointColour && sellPointColour.indexOf('#') > -1 ? sellPointColour : `#${sellPointColour}`}`
              }}>
              <div
                className={styles.sellPoint}
                style={{
                  color: `${sellPointColour && sellPointColour.indexOf('#') > -1 ? sellPointColour : `#${sellPointColour}`}`
                }}>
                <span>{sellPointName}</span>
              </div>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  const renderDefaultPrice = () => {
    return (
      <Flex direction="row" align="center">
        <PriceText
          value={price || 0}
          precision={getPrecision(price || 0)}
          showSymbol={false}
          showIcon
          className={styles.priceText}
          unitClassName={styles.priceTextSmall}
          integerClassName={styles.priceTextBig}
          decimalClassName={styles.priceTextSmall}
          tail={() => {
            if (type != 6 || price >= maxPrice) return null;
            return <div className={styles.priceExtra}>起</div>;
          }}
        />
      </Flex>
    );
  };

  const renderConsumerPrice = () => {
    const consumerOriginPrice = price || 0;
    const consumerPrice = discountSalePrice || price || 0;
    const consumerSavePriceValue =
      discountSalePrice && consumerOriginPrice > consumerPrice
        ? consumerOriginPrice - consumerPrice
        : 0;

    const consumerSavePriceText = `${formatPrice(consumerSavePriceValue)}元`;

    return (
      <div className={styles.consumerPriceBlock}>
        <Flex direction="row" align="end" className={styles.consumerPriceRow}>
          <PriceText
            value={consumerPrice || 0}
            precision={getPrecision(consumerPrice || 0)}
            showSymbol={false}
            showIcon
            className={styles.consumerPriceText}
            unitClassName={styles.consumerPriceTextSmall}
            integerClassName={styles.consumerPriceTextBig}
            decimalClassName={styles.consumerPriceTextSmall}
            tail={() => {
              if (type != 6 || consumerPrice >= maxPrice) return null;
              return <div className={styles.consumerPriceExtra}>起</div>;
            }}
          />
          <span className={styles.consumerMemberLabel}>会员价</span>
        </Flex>
        {price > 0 && (
          <div className={styles.consumerOriginPrice}>
            原价：{Util.formatMoney({ value: formatPrice(price), precision: 0 })}
          </div>
        )}
        <Flex align="center" justify="between" className={styles.consumerSaveBar}>
          <span className={styles.consumerSaveText}>立省 {consumerSavePriceText}</span>
          <span className={styles.consumerSaveActionWrap}>
            <img className={styles.consumerSaveAction} src={grabImg} alt="抢" />
          </span>
        </Flex>
      </div>
    );
  };

  return (
    <Flex
      direction="column"
      style={{
        width,
        background: '#fff',
        borderRadius: radius,
        overflow: 'hidden'
      }}
      onClick={() => handlePress()}>
      {/* 商品图片 */}
      <div
        className={styles.imageWrapper}
        style={{
          width,
          height: width
        }}>
        <ImgWrap w={width} h={width} src={imgUrl} mode="cover" />
        {/* 角标组件 */}
        {cornerMarkerRespDto ? (
          <CornerMarker
            visable={cornerMarkerRespDto.status == 1}
            className={styles.corner_img}
            style={{
              color: cornerMarkerRespDto.weighColor,
              backgroundColor: cornerMarkerRespDto.backgroundColor
            }}
            data={cornerMarkerRespDto.name}
          />
        ) : null}
      </div>
      <Flex
        direction="column"
        align="start"
        className={isConsumerActivity ? styles.consumerWrapper : styles.wrapper}>
        {reverse && (
          <React.Fragment>
            {renderSellPoint()}
            <div className={isConsumerActivity ? styles.consumerName : styles.name}>
              <span>{name}</span>
            </div>
          </React.Fragment>
        )}
        {!reverse && (
          <React.Fragment>
            <div className={isConsumerActivity ? styles.consumerName : styles.name}>
              <span>{name}</span>
            </div>
            {renderSellPoint()}
          </React.Fragment>
        )}

        {/* 消费活动卡片走独立价格区，普通商品继续保持原价样式。 */}
        {isConsumerActivity ? renderConsumerPrice() : renderDefaultPrice()}
      </Flex>
    </Flex>
  );
});

GoodItem.defaultProps = defaultProps;
GoodItem.propTypes = propTypes;

export default GoodItem;



// WEBPACK FOOTER //
// ./src/pubBiz/CommonCell/components/GoodItem/index.js